import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminAPI, schoolAPI } from "../services/api";
import "./UserManagementPage.css";

const ROLES = ["teacher", "principal", "admin"];

const ROLE_LABEL = { teacher: "Teacher", principal: "Principal", admin: "Admin" };

const roleBadgeClass = (role) => {
  if (role === "admin") return "um-role-badge um-role-admin";
  if (role === "principal") return "um-role-badge um-role-principal";
  return "um-role-badge um-role-teacher";
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline edit panel — rendered as an extra table row below the user row
// ─────────────────────────────────────────────────────────────────────────────
function EditPanel({ user, schools, onSaved, onCancel }) {
  const [role, setRole] = useState(user.role || "teacher");
  const [isActive, setIsActive] = useState(user.is_active !== false);
  // School slots: [primaryId, secondaryId | ""]
  const [schoolSlots, setSchoolSlots] = useState(() => {
    const primary = user.school_id || "";
    return [primary, ""];
  });
  const [showSecond, setShowSecond] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const handleRoleChange = (r) => {
    setRole(r);
    if (r === "admin") {
      // Admins don't belong to a school
      setSchoolSlots(["", ""]);
      setShowSecond(false);
    } else if (r === "teacher") {
      // Teachers get one school — keep primary, drop second
      setSchoolSlots((prev) => [prev[0], ""]);
      setShowSecond(false);
    }
    // principal: keep existing slots as-is
    setMsg({ type: "", text: "" });
  };

  const setSlot = (idx, val) => {
    setSchoolSlots((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleSave = async () => {
    if (role === "principal" && !schoolSlots[0]) {
      setMsg({ type: "error", text: "Select at least one school." });
      return;
    }
    const schoolIds = schoolSlots.filter(Boolean);
    setSaving(true);
    setMsg({ type: "", text: "" });
    try {
      const updated = await adminAPI.updateUser(user.id, {
        role,
        isActive,
        ...(role === "principal" ? { schoolIds } : {}),
        ...(role === "teacher" ? { schoolId: schoolSlots[0] || null } : {}),
      });
      setMsg({ type: "ok", text: "Saved." });
      onSaved(updated.data);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="um-edit-row">
      <td colSpan={6}>
        <div className="um-edit-panel">

          {/* Role */}
          <div className="um-edit-section">
            <span className="um-edit-label">Role</span>
            <div className="um-role-group">
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`um-role-btn ${role === r ? "selected" : ""}`}
                  onClick={() => handleRoleChange(r)}
                >
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
          </div>

          {/* School assignment — shown for teacher (single) and principal (multi) */}
          {(role === "teacher" || role === "principal") && (
            <div className="um-edit-section">
              <span className="um-edit-label">School</span>
              <div className="um-school-selectors">
                {role === "teacher" ? (
                  <div className="um-school-row">
                    <select
                      className="um-school-select"
                      value={schoolSlots[0]}
                      onChange={(e) => setSlot(0, e.target.value)}
                    >
                      <option value="">No school assigned</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="um-school-row">
                      <select
                        className="um-school-select"
                        value={schoolSlots[0]}
                        onChange={(e) => setSlot(0, e.target.value)}
                      >
                        <option value="">Select primary school…</option>
                        {schools.map((s) => (
                          <option key={s.id} value={s.id} disabled={s.id === schoolSlots[1]}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {showSecond ? (
                      <div className="um-school-row">
                        <select
                          className="um-school-select"
                          value={schoolSlots[1]}
                          onChange={(e) => setSlot(1, e.target.value)}
                        >
                          <option value="">Select second school (optional)…</option>
                          {schools.map((s) => (
                            <option key={s.id} value={s.id} disabled={s.id === schoolSlots[0]}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="um-remove-school-btn"
                          onClick={() => { setSlot(1, ""); setShowSecond(false); }}
                          title="Remove second school"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="um-add-school-link"
                        onClick={() => setShowSecond(true)}
                      >
                        + Add second school
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Active status */}
          <div className="um-edit-section">
            <span className="um-edit-label">Status</span>
            <div className="um-status-group">
              <button
                type="button"
                className={`um-status-btn ${isActive ? "selected-active" : ""}`}
                onClick={() => setIsActive(true)}
              >
                Active
              </button>
              <button
                type="button"
                className={`um-status-btn ${!isActive ? "selected-inactive" : ""}`}
                onClick={() => setIsActive(false)}
              >
                Inactive
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="um-edit-actions">
            <button type="button" className="um-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button type="button" className="um-cancel-btn" onClick={onCancel}>
              Cancel
            </button>
            {msg.text && (
              <span className={`um-edit-msg um-edit-msg-${msg.type === "ok" ? "ok" : "err"}`}>
                {msg.text}
              </span>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
const UserManagementPage = ({ authProfile }) => {
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("true");  // default: show active
  const [editingUserId, setEditingUserId] = useState(null);
  const debounceRef = useRef(null);

  // Load schools once (needed for principal assignment)
  useEffect(() => {
    schoolAPI.getAll().then((res) => setSchools(res.data || [])).catch(console.error);
  }, []);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    adminAPI
      .listUsers({ q: query, role: roleFilter, active: activeFilter || undefined })
      .then((res) => setUsers(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [query, roleFilter, activeFilter]);

  // Debounce search; fetch immediately on filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchUsers, 300);
    return () => clearTimeout(debounceRef.current);
  }, [fetchUsers]);

  const handleSaved = (updatedUser) => {
    setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)));
    setEditingUserId(null);
  };

  if (authProfile?.role !== "admin") {
    return (
      <div className="panel-card">
        <p className="admin-error">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="panel-card um-page">
      <h2 style={{ margin: "0 0 4px" }}>User Management</h2>
      <p style={{ margin: "0 0 20px", fontSize: 14, color: "#647490" }}>
        Manage roles, school assignments, and account status for all users.
      </p>

      {/* Filters */}
      <div className="um-filters">
        <input
          type="search"
          className="um-search"
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search users"
        />
        <select
          className="um-filter-select"
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setEditingUserId(null); }}
          aria-label="Filter by role"
        >
          <option value="">All roles</option>
          <option value="teacher">Teachers</option>
          <option value="principal">Principals</option>
          <option value="admin">Admins</option>
        </select>
        <select
          className="um-filter-select"
          value={activeFilter}
          onChange={(e) => { setActiveFilter(e.target.value); setEditingUserId(null); }}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <span className="um-count">
          {loading ? "Loading…" : `${users.length} user${users.length !== 1 ? "s" : ""}`}
        </span>
      </div>

      {/* Table */}
      <div className="um-table-wrap">
        <table className="um-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>School</th>
              <th>Status</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={6} className="um-empty">No users matched your filters.</td>
              </tr>
            )}
            {users.map((user) => (
              <React.Fragment key={user.id}>
                <tr className={editingUserId === user.id ? "um-row-editing" : ""}>
                  <td>{user.display_name || <span style={{ color: "#9ca3af" }}>—</span>}</td>
                  <td style={{ fontSize: 13, color: "#475569" }}>{user.email}</td>
                  <td>
                    <span className={roleBadgeClass(user.role)}>
                      {ROLE_LABEL[user.role] || user.role}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: "#475569" }}>
                    {user.school_name
                      ? <>{user.school_name}</>
                      : <span style={{ color: "#9ca3af" }}>—</span>}
                  </td>
                  <td>
                    <span className={`um-active-badge ${user.is_active ? "um-active-yes" : "um-active-no"}`}>
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="um-edit-btn"
                      onClick={() => setEditingUserId(editingUserId === user.id ? null : user.id)}
                    >
                      {editingUserId === user.id ? "Close" : "Edit"}
                    </button>
                  </td>
                </tr>
                {editingUserId === user.id && (
                  <EditPanel
                    user={user}
                    schools={schools}
                    onSaved={handleSaved}
                    onCancel={() => setEditingUserId(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagementPage;
